import {logger} from "src/logger";
import {
    BulkFileUploadHeaders,
    MAX_DERIVED_ATTRIBUTE_ENTITIES,
    StringUtils,
    UpsertSegmentationDerivedAttributeOperationType
} from "src/constant/constants";
import {EventModelState} from "src/state/slice/EventModelSlice";
import {getStage, isNullOrUndefined} from "src/util/commonUtil";
import {Stage} from "src/enum/enums";
import {getStageBasedAssetCloudfrontDistribution} from "src/constant/endpoints";
import {upsertDerivedSegmentationAttribute} from "src/service/service";
import {IAttachmentMetadata, UpsertDerivedSegmentationAttribute} from "src/entity/entities";

export interface ContactAlertsJsonLogic {
    and?: ContactAlertsJsonLogic[]; // An array of ContactAlertsJsonLogic objects for 'and' conditions
    or?: ContactAlertsJsonLogic[]; // An array of ContactAlertsJsonLogic objects for 'or' conditions
    "=="?: [{ var: string }, any] | []; // A condition with a variable and a value, or an empty array
}

/**
 * This function reads chunked file and returns file chunk
 * @param file is the file to be read
 */
export const readFileContents = (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.result) {
                resolve(reader.result.toString());
            } else {
                reject(new Error('Failed to read file contents'));
            }
            reader.abort();
        };
        reader.onerror = () => {
            reject(reader.error);
            reader.abort();
        };
        reader.onabort = () => {
            logger.error('FileReader aborted');
        };
        reader.readAsText(file);
    });
};

/**
 * This function converts csv to json
 * @param csv is the csv file to be converted to json
 */
export const csvToJSON = (csv: string): {jsonData: any[], entityHeader: string} => {
    const lines = csv.split(StringUtils.CARRIAGE_RETURN);
    let result = [];
    const headers = lines[0].split(StringUtils.COMMA_SEPARATOR);

    for (let i = 1; i < lines.length; i++) {
        let obj = {};

        if(isNullOrUndefined(lines[i]) || lines[i].trim() == StringUtils.EMPTY) {
            continue;
        }

        const words = lines[i].split(StringUtils.COMMA_SEPARATOR);
        for(let j = 0; j < words.length; j++) {
            obj[headers[j].trim().replace(/\s/g, StringUtils.EMPTY).toLowerCase()] =
                words[j].replace(/\r+$/, StringUtils.EMPTY);
        }

        result.push(obj);
    }
    return {jsonData: result, entityHeader: headers[0].trim().replace(/\s/g, StringUtils.EMPTY).toLowerCase()};
}

/**
 * This function converts json to csv
 * @param jsonArray is the json object to be converted to csv
 */
export const jsonArrayToCSVForCAFileUploadReport = (jsonArray: any[]): string => {
    const headers = jsonArray.length > 0 ? Object.keys(jsonArray[0]) : [];
    headers.push(BulkFileUploadHeaders.COMMENT);
    const csvRows = [headers.join(StringUtils.COMMA_SEPARATOR)];

    for (const obj of jsonArray) {
        const values = headers.map((header) => {
            const value = obj[header];
            if (isNullOrUndefined(value)) {
                return StringUtils.EMPTY;
            }
            return value.toString();
        });
        csvRows.push(values.join(StringUtils.COMMA_SEPARATOR));
    }
    return csvRows.join(StringUtils.CARRIAGE_RETURN);
}

/**
 * This function converts string to File object
 * @param content is the actual content to be converted to file object
 * @param fileName is the name of the file created
 */
export const stringToFileObject = (content: string, fileName: string): File => {
    const blob = new Blob([content], { type: 'text/plain' });
    return new File([blob], fileName, { type: 'text/plain' });
}

/**
 * This function validates the csv file uploaded by content author for contact alerts
 * @param data is a jsonArray created by reading the csv
 * @returns arrays corresponding to data with comments, valid data, invalid data, published data and unpublished data
 */
export const validateUploadedData = (data: any[]): { data: any[], validData: any[], invalidData: any[], publish: any[], unPublish: any[] } => {
    const entityIdMap = new Map<string, { count: number, indexes: number[] }>();

    for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const entityId = item[BulkFileUploadHeaders.SITE_ID] || item[BulkFileUploadHeaders.EMPLOYEE_ID];

        if (isNullOrUndefined(entityId) || entityId === StringUtils.EMPTY) {
            item[BulkFileUploadHeaders.COMMENT] = "Null Employee Id / Site Id";
        } else {
            const entityIdInfo = entityIdMap.get(entityId) || { count: 0, indexes: [] };
            entityIdInfo.count++;
            entityIdInfo.indexes.push(i);
            entityIdMap.set(entityId, entityIdInfo);

            if (entityIdInfo.count > 1) {
                for (const index of entityIdInfo.indexes) {
                    data[index][BulkFileUploadHeaders.COMMENT] = "Duplicate EmployeeId / Site Id";
                }
            }
        }
    }
    return splitValidDataByPublishStatus(data);
}

/**
 * Splits the uploaded data corresponding to data with comments, valid data, invalid data, published data and unpublished data
 * @param data is a jsonArray created by reading the csv
 * @returns arrays corresponding to data with comments, valid data, invalid data, published data and unpublished data
 */
export const splitValidDataByPublishStatus = (data: any[]): { data: any[], validData: any[], invalidData: any[], publish: any[], unPublish: any[] } => {
    const validDataMap = new Map<any, { "publish/unpublish": string, "comment"?: string }>();
    const invalidData: any[] = [];
    for (const item of data) {
        const { "comment": comment, "publish/unpublish": publishStatus, ...rest } = item;
        const value = { "publish/unpublish": publishStatus, "comment": comment };
        if (comment) {
            invalidData.push({ ...rest, ...value });
        } else {
            validDataMap.set(rest, value);
        }
    }
    const publish: any[] = [];
    const unPublish: any[] = [];
    for (const [key, value] of validDataMap.entries()) {
        if (value[BulkFileUploadHeaders.PUBLISH_UNPUBLISH].toLowerCase() === "true") {
            publish.push(key);
        } else {
            unPublish.push(key);
        }
    }
    const validData = Array.from(validDataMap.keys());
    return { data, validData, invalidData, publish, unPublish };
};

/**
 * This function creates json logic expression corresponding to the files uploaded
 * @param eventModelState is the state of the event model, used to extract event name for each json logic attribute
 * @param dataToBePublished is the list of entity ids for which contact alert needs to be published
 * @return json logic expression for bulk file upload for contact alerts
 */
export const createJsonLogicExpressionForBulkFileUpload = (eventModelState: EventModelState, dataToBePublished: any[]) => {
    const totalDerivedAttributes = Math.ceil(dataToBePublished.length / MAX_DERIVED_ATTRIBUTE_ENTITIES);
    const derivedAttributesList = [];
    for (let i = 1 ; i <= totalDerivedAttributes; i++) {
        const derivedAttributeName = createDerivedAttributeNameForBulkFileUpload(eventModelState, i);
        const derivedAttributeJsonLogic = {
            "==": [
                {
                    "var": derivedAttributeName
                }, true
            ]
        }
        derivedAttributesList.push(derivedAttributeJsonLogic);
    }
    const derivedAttributeEventRule = {
        "or": derivedAttributesList
    }
    return JSON.stringify(derivedAttributeEventRule);
}

/**
 * This function created the derived segmentation attribute names for contact alerts bulk file upload
 * @param eventModelState is the state of the event model used to extract event name
 * @param index is the ith derived segmentation attribute
 * @returns the name of derived segmentation attribute
 */
export const createDerivedAttributeNameForBulkFileUpload = (eventModelState: EventModelState, index: number) => {
    return eventModelState?.eventName + StringUtils.UNDERSCORE + getStage() as Stage + StringUtils.UNDERSCORE + index;
}

/**
 * This function induces a sleep of ms milliseconds
 * @param ms is the milliseconds for which main program should be halted
 */
export function sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
    });
}

/**
 * This function is responsible to concatenate existing event rule with json logic attributes created for bulk file upload
 * @param eventModelState to fetch existing event rule
 * @param derivedAttributeRule is the json logic created during bulk file upload
 * @return single json logic combining event rule and derived json logic expression
 */
export const combineJsonLogic = (eventModelState: EventModelState, derivedAttributeRule: string) => {
    let finalEventRule = {};
    try {
        let currentEventRule = {};
        if (!isNullOrUndefined(eventModelState?.eventRule) && eventModelState?.eventRule !== StringUtils.EMPTY) {
            currentEventRule = JSON.parse(eventModelState?.eventRule); // event rule is a json
        }
        const derivedAttributeRuleObject = JSON.parse(derivedAttributeRule);
        if (!currentEventRule) {
            return derivedAttributeRuleObject;
        }
        if (Object.keys(currentEventRule).length === 0) {
            finalEventRule = {
                "and": [derivedAttributeRuleObject]
            }
        }
        else if (currentEventRule["and"]) {
            finalEventRule = {
                "and": [...currentEventRule["and"], derivedAttributeRuleObject]
            }
        } else if (currentEventRule["or"]) {
            finalEventRule = {
                "or": [...currentEventRule["or"], derivedAttributeRuleObject]
            }
        } // if it is not an "and" or "or" operator, then it will be a single key value pair
        else {
            finalEventRule = {
                "and": [
                    currentEventRule,
                    derivedAttributeRuleObject
                ]
            }
        }
        return finalEventRule;
    } catch (error) {
        logger.error(`Error parsing JSON: ${eventModelState?.eventRule}`, error);
    }
}

/**
 * This function extracts file names, file type and constructs file links from the uploaded files
 * @param uploadedFileNames is the name of all files uploaded
 * @param eventModelState is the event model definition to construct file links
 * @returns json array having file link, file name and file type for all uploaded files
 */
export const extractUploadedFilesMetadata = (uploadedFileNames: string, eventModelState: EventModelState): IAttachmentMetadata[] => {
    let uploadedFilesMetadata: IAttachmentMetadata[] = [];
    const fileNames: string[] = uploadedFileNames.split(StringUtils.COMMA_SEPARATOR);
    fileNames.forEach(fileName => {
        const link: string = constructFileLink(fileName, eventModelState);
        const fileSeparators: string[] = fileName.split(StringUtils.DOT_SEPARATOR);
        const type: string = fileSeparators[fileSeparators.length - 1];
        uploadedFilesMetadata.push({fileName, link, type})
    })
    return uploadedFilesMetadata;
}

/**
 * This function is responsible to construct file link for the uploaded file
 * @param fileName is the name of the file uploaded
 * @param eventModelState is the state of event model, used to create folder partitions in Asset Management Service S3 bucket
 * @returns file link of the uploaded file in asset management service
 */
export const constructFileLink = (fileName: string, eventModelState: EventModelState) => {
    return getStageBasedAssetCloudfrontDistribution(getStage() as Stage) + StringUtils.SLASH_DELIMITER +
        eventModelState?.eventName + StringUtils.SLASH_DELIMITER + fileName.trim();
}

/**
 * This recursive function is responsible to clear all the derived segmentation attributes from event rule if the uploaded file for bulk upload is cleared
 * @param logic is the event rule in json format
 * @param derivedSegmentationAttributes is the list of derived segmentation attributes that need to be removed
 * @returns event rule json object that is free from derived segmentation attributes
 */
export const modifyEventRuleOnUploadedBulkFileRemoval = (logic: ContactAlertsJsonLogic, derivedSegmentationAttributes: string[]): ContactAlertsJsonLogic => {
    // Case 1: If the logic has an 'and' condition
    if (logic.and) {
        const transformedAndConditions = logic.and
            .map((condition) => modifyEventRuleOnUploadedBulkFileRemoval(condition, derivedSegmentationAttributes)) // Recursively transform each 'and' condition
            .filter((condition) => {
                // Filter out empty 'or' conditions
                if (condition.or) {
                    return condition.or.length > 0;
                }
                return true; // Keep all other conditions
            });
        return { and: transformedAndConditions }; // Return the transformed 'and' conditions
    }
    // Case 2: If the logic has an 'or' condition
    else if (logic.or) {
        const transformedOrConditions = logic.or
            .map((condition) => modifyEventRuleOnUploadedBulkFileRemoval(condition, derivedSegmentationAttributes)) // Recursively transform each 'or' condition
            .filter(
                (condition) =>
                    // Filter out conditions where the value is true and the variable is in the derivedSegmentationAttributes array
                    condition["=="] &&
                    (condition["=="][1] !== true ||
                        !derivedSegmentationAttributes.includes(condition["=="][0].var))
            );
        return { or: transformedOrConditions }; // Return the transformed 'or' conditions
    }
    // Case 3: If the logic has a single condition
    else if (Array.isArray(logic["=="])) {
        const [{ var: variable }] = logic["=="]; // Get the variable from the condition
        if (derivedSegmentationAttributes.includes(variable)) {
            return {}; // If the variable is in the derivedSegmentationAttributes array, return an empty object to remove the condition
        }
    }
    return logic; // If none of the above cases match, return the original logic
}

/**
 * Recursive function to extract derived segmentation attributes for bulk file upload from the event rule
 * @param eventRuleObject is the event rule in json format containing derived segmentation attributes as well
 * @param eventName is the event name which represents prefix for bulk file upload derived segmentation attributes
 * @returns list of derived segmentation attributes as a part of bulk file upload
 */
export const extractBulkFileUploadSegmentationAttributes = (eventRuleObject: any, eventName: string): string[] => {
    const result: string[] = [];

    const traverse = (value: any) => {
        if (Array.isArray(value)) {
            value.forEach((item) => traverse(item));
        } else if (typeof value === 'object' && value !== null) {
            if (typeof value === 'string' && value.includes(eventName)) {
                result.push(value);
            } else if (value.hasOwnProperty('var')) {
                const varValue = typeof value.var === 'object' ? value.var.var : value.var;
                if (typeof varValue === 'string' && varValue.includes(eventName)) {
                    result.push(varValue);
                }
            }
            Object.values(value).forEach((item) => traverse(item));
        }
    }

    traverse(eventRuleObject);
    return result;
}

/**
 * This function is responsible to create derived segmentation attributes for all entity ids eligible for contact alerts
 * @param eventModelState is the state of the event model to create derived segmentation attribute names
 * @param dataToBePublished is the actual list of employee / site ids against which derived segmentation attributes need to be created
 * @param entityHeader is the represents the "var" for which segmentation attributes need to be created
 * @param existingBulkFileUploadAttributes helps determining whether a new attribute needs to be created or an existing attribute needs to be updated
 */
export const upsertDerivedSegmentationAttributes = async (eventModelState: EventModelState, dataToBePublished: any[], entityHeader: string, existingBulkFileUploadAttributes: string[]) => {
    let index: number = 0;
    // TODO: make parallel API calls
    for (let i: number = 0; i < dataToBePublished.length; i += MAX_DERIVED_ATTRIBUTE_ENTITIES) {
        const attributeName: string = createDerivedAttributeNameForBulkFileUpload(eventModelState, index + 1);
        const entityIds: string[] = dataToBePublished.slice(i, i + MAX_DERIVED_ATTRIBUTE_ENTITIES);
        const variable: string = entityHeader === BulkFileUploadHeaders.EMPLOYEE_ID ? "employeeId" : "buildingCode"
        const definition: string = JSON.stringify({
            "in": [
                {
                    "var": variable
                }, entityIds.map(obj => Object.values(obj)[0])
            ]
        });
        const operationType: string = existingBulkFileUploadAttributes.includes(attributeName) ?
            UpsertSegmentationDerivedAttributeOperationType.UPDATE :
            UpsertSegmentationDerivedAttributeOperationType.CREATE;
        const description: string = `Derived attribute #${index + 1} for Contact Alert event: ${eventModelState?.eventName}.`;
        try {
            const upsertDerivedSegmentationAttributeResponse: UpsertDerivedSegmentationAttribute =
                await upsertDerivedSegmentationAttribute(attributeName, description, variable.split(","),
                    definition, operationType);
            logger.info("upsert attribute response: " + upsertDerivedSegmentationAttributeResponse);
            logger.info(`attribute upserted: ${attributeName}, ${operationType.toString()}`);
            await sleep(500);
        } catch (error) {
            console.error(`Error ${operationType} derived attribute: ${attributeName}`, error);
        }
        index++;
    }
}
